import { z } from 'zod'
import { optionalText, pageQuerySchema, versionSchema } from './common.js'
import type { ModuleKey, Permission } from './permissions.js'

/**
 * Tasks (ADR-021, TASKS.md). A team owns the responsibility, one person owns the execution, and a task
 * can belong to a customer and any number of ERP records.
 */

// ── statuses, moves and the facts kept apart from status ─────────────────────

export const TASK_STATUSES = [
  'BACKLOG',
  'TODO',
  'IN_PROGRESS',
  'IN_REVIEW',
  'COMPLETED',
  'CANCELLED',
] as const
export const taskStatusSchema = z.enum(TASK_STATUSES)
export type TaskStatus = z.infer<typeof taskStatusSchema>

/** Statuses of work not yet finished: overdue, blocked and unassigned only mean something here. */
export const OPEN_TASK_STATUSES = [
  'BACKLOG',
  'TODO',
  'IN_PROGRESS',
  'IN_REVIEW',
] as const satisfies readonly TaskStatus[]
export const CLOSED_TASK_STATUSES = ['COMPLETED', 'CANCELLED'] as const satisfies readonly TaskStatus[]

export function isOpenTaskStatus(status: TaskStatus): boolean {
  return (OPEN_TASK_STATUSES as readonly TaskStatus[]).includes(status)
}

/** The board's columns, in order. Cancelled tasks are left off the board. */
export const BOARD_STATUSES = [
  'BACKLOG',
  'TODO',
  'IN_PROGRESS',
  'IN_REVIEW',
  'COMPLETED',
] as const satisfies readonly TaskStatus[]

/**
 * The named moves of the task workflow (TASKS.md §4). One table serves buttons, the board, bulk
 * actions and the API (`POST /tasks/:id/move`), so no surface can skip a rule.
 */
export type TaskMove =
  | 'commit'
  | 'backlog'
  | 'start'
  | 'pause'
  | 'submit'
  | 'complete'
  | 'accept'
  | 'return'
  | 'cancel'
  | 'reopen'

/**
 * The move that takes a task from `from` to `to`, or null when there is none. Review decides between
 * submit and complete: a task that needs review only reaches COMPLETED through the reviewer's accept.
 */
export function taskMoveFor(from: TaskStatus, to: TaskStatus, reviewRequired: boolean): TaskMove | null {
  if (from === to) return null
  if (to === 'CANCELLED') return isOpenTaskStatus(from) ? 'cancel' : null
  if (from === 'COMPLETED' || from === 'CANCELLED') return to === 'TODO' || to === 'BACKLOG' ? 'reopen' : null
  switch (`${from}>${to}`) {
    case 'BACKLOG>TODO':
      return 'commit'
    case 'TODO>BACKLOG':
      return 'backlog'
    case 'TODO>IN_PROGRESS':
      return 'start'
    case 'IN_PROGRESS>TODO':
      return 'pause'
    case 'IN_PROGRESS>IN_REVIEW':
      return reviewRequired ? 'submit' : null
    case 'IN_PROGRESS>COMPLETED':
    case 'TODO>COMPLETED':
      return reviewRequired ? null : 'complete'
    case 'IN_REVIEW>COMPLETED':
      return 'accept'
    case 'IN_REVIEW>IN_PROGRESS':
      return 'return'
    default:
      return null
  }
}

/** Moves that need words from the person making them: a reason, or the reviewer's feedback. */
export const MOVES_NEEDING_REASON: readonly TaskMove[] = ['cancel', 'reopen', 'return']

export const TASK_PRIORITIES = ['LOW', 'NORMAL', 'HIGH', 'URGENT'] as const
export const taskPrioritySchema = z.enum(TASK_PRIORITIES)
export type TaskPriority = z.infer<typeof taskPrioritySchema>

/**
 * Who sees a task besides its people (creator, requester, assignee, reviewer, contributors,
 * followers) and holders of tasks.view_all (TASKS.md §5).
 *   PERSONAL    nobody else; the task has no team
 *   TEAM        members of the responsible team
 *   RESTRICTED  the responsible team's leads only
 *   PROJECT     the active members of the project the task is linked to (and the team's members when it
 *               has one); project managers act as leads (ADR-039). Needs a `project` link and the
 *               Projects module; without them it falls back to TEAM (with a team) or PERSONAL.
 */
export const TASK_VISIBILITIES = ['PERSONAL', 'TEAM', 'RESTRICTED', 'PROJECT'] as const
export const taskVisibilitySchema = z.enum(TASK_VISIBILITIES)
export type TaskVisibility = z.infer<typeof taskVisibilitySchema>

/** How widely a visibility shares: a subtask can never be seen by more people than its parent. */
export const TASK_VISIBILITY_RANK: Record<TaskVisibility, number> = {
  PERSONAL: 1,
  RESTRICTED: 1,
  TEAM: 2,
  PROJECT: 2,
}

/** Kinds of work. Customer, urgency and progress are other fields, never types. */
export const TASK_TYPES = [
  'GENERAL',
  'FOLLOW_UP',
  'IMPLEMENTATION',
  'MAINTENANCE',
  'DOCUMENT_PREPARATION',
  'INTERNAL',
] as const
export const taskTypeSchema = z.enum(TASK_TYPES)
export type TaskType = z.infer<typeof taskTypeSchema>

export const TASK_TYPE_LABELS: Record<TaskType, { en: string; ar: string }> = {
  GENERAL: { en: 'General', ar: 'عام' },
  FOLLOW_UP: { en: 'Follow-up', ar: 'متابعة' },
  IMPLEMENTATION: { en: 'Implementation', ar: 'تنفيذ' },
  MAINTENANCE: { en: 'Maintenance', ar: 'صيانة' },
  DOCUMENT_PREPARATION: { en: 'Document preparation', ar: 'إعداد مستندات' },
  INTERNAL: { en: 'Internal administration', ar: 'إدارة داخلية' },
}

export const TASK_PERSON_ROLES = ['CONTRIBUTOR', 'FOLLOWER'] as const
export type TaskPersonRole = (typeof TASK_PERSON_ROLES)[number]

export const TEAM_ROLES = ['LEAD', 'MEMBER'] as const
export const teamRoleSchema = z.enum(TEAM_ROLES)
export type TeamRole = z.infer<typeof teamRoleSchema>

/** No update for this many days makes an open task "stale" in reports and the register. */
export const TASK_STALE_DAYS = 7

// ── related ERP records ──────────────────────────────────────────────────────

/**
 * Records a task can be linked to (besides its one customer). Each keeps its own read
 * permission: seeing a task never shows a record the viewer may not read (TASKS.md §8).
 */
export const TASK_LINK_TYPES = [
  'quotation',
  'sales_invoice',
  'credit_note',
  'receipt',
  'purchase',
  'supplier_payment',
  'supplier_return',
  'expense',
  'supplier',
  'product',
  'service_job',
  /** At most one per task (ADR-039); its place in the project plan is kept by Projects. */
  'project',
  /** Service C4: a maintenance contract (renewal follow-ups, customer talks). */
  'service_contract',
] as const
export const taskLinkTypeSchema = z.enum(TASK_LINK_TYPES)
export type TaskLinkType = z.infer<typeof taskLinkTypeSchema>

export interface TaskLinkTypeDef {
  read: Permission
  module: ModuleKey
  en: string
  ar: string
}

export const TASK_LINK_DEFS: Record<TaskLinkType, TaskLinkTypeDef> = {
  quotation: { read: 'sales.read', module: 'sales', en: 'Quotation', ar: 'عرض سعر' },
  sales_invoice: { read: 'sales.read', module: 'sales', en: 'Invoice', ar: 'فاتورة' },
  credit_note: { read: 'sales.read', module: 'sales', en: 'Credit note', ar: 'إشعار دائن' },
  receipt: { read: 'sales.read', module: 'sales', en: 'Receipt', ar: 'سند قبض' },
  purchase: { read: 'purchasing.read', module: 'purchasing', en: 'Purchase', ar: 'مشتريات' },
  supplier_payment: {
    read: 'purchasing.read',
    module: 'purchasing',
    en: 'Supplier payment',
    ar: 'دفعة مورد',
  },
  supplier_return: { read: 'purchasing.read', module: 'purchasing', en: 'Supplier return', ar: 'مرتجع مورد' },
  expense: { read: 'expenses.read', module: 'expenses', en: 'Expense', ar: 'مصروف' },
  supplier: { read: 'suppliers.read', module: 'purchasing', en: 'Supplier', ar: 'مورد' },
  product: { read: 'products.read', module: 'catalog', en: 'Product', ar: 'منتج' },
  service_job: { read: 'service.read', module: 'service', en: 'Service job', ar: 'أمر خدمة' },
  project: { read: 'projects.read', module: 'projects', en: 'Project', ar: 'مشروع' },
  service_contract: {
    read: 'contracts.read',
    module: 'contracts',
    en: 'Maintenance contract',
    ar: 'عقد صيانة',
  },
}

export const taskLinkInputSchema = z.object({
  entityType: taskLinkTypeSchema,
  entityId: z.uuid(),
})
export type TaskLinkInput = z.infer<typeof taskLinkInputSchema>

/**
 * A linked record as the viewer may see it. Without the record type's read permission (or its module)
 * only the type is shown: `label` null, `restricted` true.
 */
export interface TaskLinkView {
  entityType: TaskLinkType
  entityId: string
  restricted: boolean
  /** The document number or the record's code. */
  number: string | null
  /** The counterparty or the record's name. */
  label: string | null
  labelAr: string | null
  /** The record's own status (e.g. POSTED, VOID), when it has one. */
  status: string | null
  /** The record was voided or cancelled since it was linked: open work may need a look. */
  voided: boolean
  /** The record no longer exists (a deleted draft). */
  missing: boolean
}

// ── references ───────────────────────────────────────────────────────────────

export interface TaskUserRef {
  id: string
  name: string
  nameAr: string | null
  /** False for a disabled user: their open tasks need reassigning. */
  active: boolean
}

export interface TaskTeamRef {
  id: string
  name: string
  nameAr: string | null
}

export interface TaskCustomerRef {
  id: string
  code: string
  name: string
  nameAr: string | null
}

// ── task input ───────────────────────────────────────────────────────────────

const timeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:MM (24-hour)')
const minutesSchema = z.number().int().min(0).max(100_000)
const tagSchema = z
  .string()
  .trim()
  .min(1)
  .max(40)
  .regex(/^[^,]+$/, 'A tag cannot contain a comma')
export const taskTagsSchema = z
  .array(tagSchema)
  .max(10)
  .transform((tags) => [...new Set(tags)])

export const taskTitleSchema = z.string().trim().min(1, 'Title is required').max(200)

/**
 * `POST /tasks`. Defaults: type GENERAL, priority NORMAL, status TODO (BACKLOG to capture work not
 * yet committed), requester = the creator, visibility TEAM with a team (PERSONAL without one; a
 * subtask's parent's), review from the company's setting. A subtask (`parentId`) takes its parent's
 * customer.
 */
export const taskCreateSchema = z.object({
  title: taskTitleSchema,
  description: optionalText(10_000),
  expectedResult: optionalText(4_000),
  type: taskTypeSchema.default('GENERAL'),
  priority: taskPrioritySchema.default('NORMAL'),
  status: z.enum(['BACKLOG', 'TODO']).default('TODO'),
  visibility: taskVisibilitySchema.optional(),
  teamId: z.uuid().nullish(),
  assigneeId: z.uuid().nullish(),
  reviewerId: z.uuid().nullish(),
  requesterId: z.uuid().nullish(),
  reviewRequired: z.boolean().optional(),
  customerId: z.uuid().nullish(),
  parentId: z.uuid().nullish(),
  /** A subtask that must be resolved before its parent can be submitted or completed. */
  required: z.boolean().default(true),
  plannedStart: z.iso.date().nullish(),
  dueDate: z.iso.date().nullish(),
  dueTime: timeSchema.nullish(),
  estimateMinutes: minutesSchema.nullish(),
  tags: taskTagsSchema.default([]),
  contributorIds: z.array(z.uuid()).max(20).default([]),
  links: z.array(taskLinkInputSchema).max(10).default([]),
  checklist: z.array(z.string().trim().min(1).max(500)).max(50).default([]),
})
export type TaskCreateInput = z.input<typeof taskCreateSchema>

/**
 * `PATCH /tasks/:id`: any subset of the details. Changing a committed due date needs `dueChangeReason`.
 * Assignment, team, status, blockers and people have their own commands.
 */
export const taskUpdateSchema = z
  .object({
    version: versionSchema,
    title: taskTitleSchema.optional(),
    description: optionalText(10_000).optional(),
    expectedResult: optionalText(4_000).optional(),
    type: taskTypeSchema.optional(),
    priority: taskPrioritySchema.optional(),
    visibility: taskVisibilitySchema.optional(),
    reviewerId: z.uuid().nullish(),
    requesterId: z.uuid().optional(),
    reviewRequired: z.boolean().optional(),
    customerId: z.uuid().nullish(),
    required: z.boolean().optional(),
    plannedStart: z.iso.date().nullish(),
    dueDate: z.iso.date().nullish(),
    dueTime: timeSchema.nullish(),
    dueChangeReason: optionalText(1_000).optional(),
    estimateMinutes: minutesSchema.nullish(),
    tags: taskTagsSchema.optional(),
  })
  .strict()
export type TaskUpdateInput = z.input<typeof taskUpdateSchema>

/** `POST /tasks/:id/assign`: the responsible team and the assignee (null: back to the team's queue). */
export const taskAssignSchema = z.object({
  version: versionSchema,
  teamId: z.uuid().nullish(),
  assigneeId: z.uuid().nullable(),
  note: optionalText(1_000),
})
export type TaskAssignInput = z.input<typeof taskAssignSchema>

/** `POST /tasks/:id/move`. Cancel, reopen and return need `reason`. */
export const taskMoveSchema = z.object({
  version: versionSchema,
  to: taskStatusSchema,
  reason: optionalText(2_000),
  /** On submit or complete: what was delivered (the completion note). */
  note: optionalText(4_000),
  /** On submit or complete: the effort actually spent, in minutes. */
  actualMinutes: minutesSchema.nullish(),
})
export type TaskMoveInput = z.input<typeof taskMoveSchema>

/** `POST /tasks/:id/block`: what stands in the way, what it waits for, who follows up and when. */
export const taskBlockSchema = z.object({
  version: versionSchema,
  reason: z.string().trim().min(1, 'Say what is blocking the work').max(1_000),
  waitingOn: optionalText(300),
  followUpOwnerId: z.uuid().nullish(),
  followUpDate: z.iso.date().nullish(),
})
export type TaskBlockInput = z.input<typeof taskBlockSchema>

export const taskUnblockSchema = z.object({ version: versionSchema, note: optionalText(1_000) })
export type TaskUnblockInput = z.input<typeof taskUnblockSchema>

/** `POST /tasks/:id/date-requests`: an assignee who may not reschedule asks for a new due date. */
export const taskDateRequestSchema = z.object({
  proposedDate: z.iso.date(),
  reason: z.string().trim().min(1, 'Say why the date should change').max(1_000),
})
export type TaskDateRequestInput = z.input<typeof taskDateRequestSchema>

export const taskClaimSchema = z.object({ version: versionSchema })

export const taskCommentSchema = z.object({
  body: z.string().trim().min(1, 'Write a comment').max(4_000),
})
export type TaskCommentInput = z.input<typeof taskCommentSchema>

export const taskPersonSchema = z.object({
  userId: z.uuid(),
  role: z.enum(TASK_PERSON_ROLES).default('CONTRIBUTOR'),
})

export const checklistItemCreateSchema = z.object({ text: z.string().trim().min(1).max(500) })
export const checklistItemUpdateSchema = z
  .object({
    text: z.string().trim().min(1).max(500).optional(),
    done: z.boolean().optional(),
    position: z.number().int().min(0).max(10_000).optional(),
  })
  .strict()

// ── task lists ───────────────────────────────────────────────────────────────

/**
 * Built-in views of the register (the tabs). `open` is the default; `mine` is the viewer's open
 * assigned work; `review` is submitted work waiting for the viewer's decision.
 */
export const TASK_VIEWS = [
  'open',
  'mine',
  'unassigned',
  'overdue',
  'blocked',
  'review',
  'completed',
  'cancelled',
  'all',
] as const
export type TaskView = (typeof TASK_VIEWS)[number]

/** `me`, `none` (no one) or a user id. */
const personFilter = z.union([z.literal('me'), z.literal('none'), z.uuid()])

export const TASK_DUE_FILTERS = ['overdue', 'today', 'week', 'later', 'none'] as const
export type TaskDueFilter = (typeof TASK_DUE_FILTERS)[number]

const taskFilterFields = {
  view: z.enum(TASK_VIEWS).default('open'),
  status: taskStatusSchema.optional(),
  priority: taskPrioritySchema.optional(),
  type: taskTypeSchema.optional(),
  teamId: z.uuid().optional(),
  assigneeId: personFilter.optional(),
  requesterId: personFilter.optional(),
  reviewerId: personFilter.optional(),
  customerId: z.uuid().optional(),
  /** A task's subtasks; `none` for top-level tasks only. */
  parentId: z.union([z.literal('none'), z.uuid()]).optional(),
  due: z.enum(TASK_DUE_FILTERS).optional(),
  dueFrom: z.iso.date().optional(),
  dueTo: z.iso.date().optional(),
  /** Completed within (business dates, inclusive): report drill-down. */
  completedFrom: z.iso.date().optional(),
  completedTo: z.iso.date().optional(),
  blocked: z.stringbool().optional(),
  /**
   * Open blocked tasks the viewer is assigned to or follows the blocker up on (what My work's Blocked
   * tile counts); `false` filters nothing.
   */
  myBlocked: z.stringbool().optional(),
  following: z.stringbool().optional(),
  /** Open with no meaningful update for TASK_STALE_DAYS days. */
  stale: z.stringbool().optional(),
  /** Open tasks whose assignee's account is disabled: to reassign. */
  inactiveAssignee: z.stringbool().optional(),
  /** Completed after their committed due date (report drill-down). */
  late: z.stringbool().optional(),
  /** Completed on or before their committed due date (report drill-down: on-time delivery). */
  onTime: z.stringbool().optional(),
  /** Committed due date within (business dates, inclusive): what a period's on-time figure measures. */
  committedFrom: z.iso.date().optional(),
  committedTo: z.iso.date().optional(),
  /** Tasks without subtasks only: the deliverables the reports count. */
  deliverable: z.stringbool().optional(),
  /** Customer work only. */
  hasCustomer: z.stringbool().optional(),
  /** Returned by a reviewer or reopened at least once (report drill-down). */
  reworked: z.stringbool().optional(),
  tag: z.string().trim().min(1).max(40).optional(),
  linkType: taskLinkTypeSchema.optional(),
  linkId: z.uuid().optional(),
}

export const taskListQuerySchema = pageQuerySchema.extend(taskFilterFields)
export type TaskListQuery = z.input<typeof taskListQuerySchema>

export const TASK_SORT_FIELDS = [
  'number',
  'title',
  'dueDate',
  'priority',
  'status',
  'lastActivityAt',
  'createdAt',
] as const

/** `GET /tasks/board`: the register's filters; each column returns up to `perColumn` cards. */
export const taskBoardQuerySchema = z.object({
  ...taskFilterFields,
  q: z.string().trim().max(200).optional(),
  view: z.enum(TASK_VIEWS).default('all'),
  perColumn: z.coerce.number().int().min(1).max(100).default(50),
  /** Completed tasks shown: those completed in the last this many days. */
  completedDays: z.coerce.number().int().min(1).max(365).default(14),
})
export type TaskBoardQuery = z.input<typeof taskBoardQuerySchema>

/** The longest period one calendar request covers (a month view shows up to six weeks). */
export const TASK_CALENDAR_MAX_DAYS = 62
/** The most tasks one calendar request returns; beyond it the answer says it was cut. */
export const TASK_CALENDAR_MAX_ITEMS = 1_000

const {
  view: _calendarView,
  due: _calendarDue,
  dueFrom: _calendarDueFrom,
  dueTo: _calendarDueTo,
  ...calendarFilterFields
} = taskFilterFields

/**
 * `GET /tasks/calendar` (TASKS.md §7): the tasks due between `from` and `to` (inclusive, business dates)
 * with the register's other filters, cancelled work left out; `scope=open` leaves completed work out
 * too. With `starts`, tasks whose planned start falls in the period come as well (scheduled work).
 * The answer also carries the open tasks with no due date (the Unscheduled panel).
 */
export const taskCalendarQuerySchema = z.object({
  ...calendarFilterFields,
  q: z.string().trim().max(200).optional(),
  from: z.iso.date(),
  to: z.iso.date(),
  scope: z.enum(['open', 'all']).default('all'),
  starts: z.stringbool().optional(),
})
export type TaskCalendarQuery = z.input<typeof taskCalendarQuerySchema>

export interface TaskCalendar {
  from: string
  to: string
  today: string
  /** Due in the period (or, with `starts`, starting in it): most urgent first within a day. */
  items: TaskListItem[]
  /** More than TASK_CALENDAR_MAX_ITEMS matched: narrow the filters. */
  truncated: boolean
  /** Open work with no due date yet, oldest first. */
  unscheduled: TaskListItem[]
  unscheduledTotal: number
}

export const TASK_EXPORT_MAX_ROWS = 20_000

export const taskExportQuerySchema = z.object({
  ...taskFilterFields,
  q: z.string().trim().max(200).optional(),
  sort: pageQuerySchema.shape.sort,
  lang: z.enum(['en', 'ar']).default('en'),
})
export type TaskExportQuery = z.input<typeof taskExportQuerySchema>

/** `GET /tasks/related`: tasks of a customer (its customer field or a link) or of a linked record. */
export const taskRelatedQuerySchema = z.object({
  entityType: z.union([z.literal('customer'), taskLinkTypeSchema]),
  entityId: z.uuid(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
})
export type TaskRelatedQuery = z.input<typeof taskRelatedQuerySchema>

export interface TaskProgress {
  total: number
  done: number
}

export interface TaskListItem {
  id: string
  number: string
  title: string
  type: TaskType
  status: TaskStatus
  priority: TaskPriority
  visibility: TaskVisibility
  team: TaskTeamRef | null
  assignee: TaskUserRef | null
  reviewer: TaskUserRef | null
  customer: TaskCustomerRef | null
  parent: { id: string; number: string } | null
  reviewRequired: boolean
  required: boolean
  dueDate: string | null
  dueTime: string | null
  /** The due date when the work was committed; when the due date moved, lists show both. */
  committedDueDate: string | null
  /** When the work is planned to start (the calendar shows it as scheduled work). */
  plannedStart: string | null
  /** Open and past its deadline (Muscat time). */
  overdue: boolean
  /** Whole days past the due date; 0 when not overdue. */
  overdueDays: number
  blocked: boolean
  /** Subtasks resolved (completed or cancelled) of all; null without subtasks. */
  subtasks: TaskProgress | null
  checklist: TaskProgress | null
  attachmentCount: number
  /** Since when the task has waited in its team's queue without an assignee (open tasks only). */
  queuedSince: string | null
  completedAt: string | null
  lastActivityAt: string
  createdAt: string
  version: number
}

export interface TaskBoardColumn {
  status: TaskStatus
  total: number
  items: TaskListItem[]
}

export interface TaskBoard {
  columns: TaskBoardColumn[]
}

/** Tab counts of the register for the same filters (each view's own condition added). */
export type TaskViewCounts = Record<TaskView, number>

export interface TaskRelated {
  items: TaskListItem[]
  counts: { open: number; overdue: number; blocked: number; completed: number }
}

// ── task detail ──────────────────────────────────────────────────────────────

export interface TaskBlocker {
  since: string
  reason: string
  waitingOn: string | null
  followUpOwner: TaskUserRef | null
  followUpDate: string | null
  by: TaskUserRef | null
}

export interface TaskChecklistItem {
  id: string
  text: string
  position: number
  done: boolean
  doneAt: string | null
  doneBy: TaskUserRef | null
}

/**
 * What the viewer may do with this task, computed by the API with the same rules it enforces; the web
 * offers exactly these.
 */
export interface TaskCapabilities {
  /** Statuses the viewer may move the task to now. */
  moves: TaskStatus[]
  edit: boolean
  assign: boolean
  claim: boolean
  reschedule: boolean
  requestDate: boolean
  block: boolean
  comment: boolean
  checklist: boolean
  attach: boolean
  links: boolean
  people: boolean
  addSubtask: boolean
  /** The viewer is asked to review (it is IN_REVIEW and they are the reviewer or a deciding lead). */
  review: boolean
}

/** The viewer's relation to the task, for the header's "You are the assignee" line. */
export type TaskViewerRole =
  | 'ASSIGNEE'
  | 'REVIEWER'
  | 'REQUESTER'
  | 'CONTRIBUTOR'
  | 'FOLLOWER'
  | 'TEAM_LEAD'
  | 'TEAM_MEMBER'
  | 'OTHER'

export interface TaskDetail extends TaskListItem {
  description: string | null
  expectedResult: string | null
  requester: TaskUserRef | null
  contributors: TaskUserRef[]
  followers: TaskUserRef[]
  estimateMinutes: number | null
  actualMinutes: number | null
  completionNote: string | null
  tags: string[]
  blocker: TaskBlocker | null
  links: TaskLinkView[]
  checklistItems: TaskChecklistItem[]
  /** Subtasks the viewer may see. */
  subtaskItems: TaskListItem[]
  /** Subtasks hidden from the viewer by their visibility. */
  hiddenSubtasks: number
  /** The parent's title when the viewer may see it. */
  parentTitle: string | null
  createdBy: TaskUserRef | null
  assignedAt: string | null
  assignedBy: TaskUserRef | null
  startedAt: string | null
  submittedAt: string | null
  completedBy: TaskUserRef | null
  cancelledAt: string | null
  cancelledBy: TaskUserRef | null
  cancelReason: string | null
  reopenCount: number
  returnCount: number
  following: boolean
  viewerRoles: TaskViewerRole[]
  can: TaskCapabilities
}

// ── activity ─────────────────────────────────────────────────────────────────

export const TASK_EVENT_TYPES = [
  'CREATED',
  'UPDATED',
  'ASSIGNED',
  'CLAIMED',
  'TEAM_CHANGED',
  'MOVED',
  'REVIEW_ACCEPTED',
  'REVIEW_RETURNED',
  'BLOCKED',
  'UNBLOCKED',
  'DUE_CHANGED',
  'DATE_REQUESTED',
  'PRIORITY_CHANGED',
  'PERSON_ADDED',
  'PERSON_REMOVED',
  'LINK_ADDED',
  'LINK_REMOVED',
  'SUBTASK_ADDED',
  'REOPENED',
  'CANCELLED',
] as const
export type TaskEventType = (typeof TASK_EVENT_TYPES)[number]

export type TaskEventData = Record<string, string | number | boolean | null | string[]>

export type TaskActivityItem =
  | { kind: 'comment'; id: string; at: string; actorId: string | null; body: string }
  | {
      kind: 'event'
      id: string
      at: string
      actorId: string | null
      type: TaskEventType
      data: TaskEventData
    }

/**
 * `GET /tasks/:id/activity`: comments and the timeline, newest first, with every person they name.
 * `?filter=comments` keeps only comments.
 */
export interface TaskActivity {
  items: TaskActivityItem[]
  users: Record<string, TaskUserRef>
  page: number
  pageSize: number
  total: number
}

export const taskActivityQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
  filter: z.enum(['all', 'comments', 'history']).default('all'),
})
export type TaskActivityQuery = z.input<typeof taskActivityQuerySchema>

// ── my work ──────────────────────────────────────────────────────────────────

export interface MyWorkCounts {
  overdue: number
  dueToday: number
  /** My blocked tasks and blockers whose follow-up I own. */
  blocked: number
  toReview: number
  assigned: number
  requested: number
  following: number
  /** Unassigned open work in the teams I belong to. */
  queue: number
}

/** `GET /tasks/my-work`: the employee's landing page. */
export interface MyWork {
  counts: MyWorkCounts
  /** Overdue, due today, and blockers I follow up, most urgent first. */
  attention: TaskListItem[]
  assigned: TaskListItem[]
  review: TaskListItem[]
  requested: TaskListItem[]
  following: TaskListItem[]
  /** The oldest unassigned work in my teams. */
  queue: TaskListItem[]
  today: string
}

/** `GET /tasks/my-work/count`: the navigation badge (overdue + due today + waiting for my review). */
export interface MyWorkBadge {
  attention: number
}

// ── people directory ─────────────────────────────────────────────────────────

export const taskPeopleQuerySchema = z.object({
  q: z.string().trim().max(100).optional(),
  /** Members of this team come first, marked. */
  teamId: z.uuid().optional(),
  /** One person by id, whatever their status (a form showing a preset). */
  id: z.uuid().optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
})
export type TaskPeopleQuery = z.input<typeof taskPeopleQuerySchema>

export interface TaskPerson extends TaskUserRef {
  /** Member of the requested team (with their role there). */
  teamRole: TeamRole | null
}

// ── reports ──────────────────────────────────────────────────────────────────

export const taskReportQuerySchema = z.object({
  from: z.iso.date(),
  to: z.iso.date(),
  teamId: z.uuid().optional(),
  customerId: z.uuid().optional(),
})
export type TaskReportQuery = z.input<typeof taskReportQuerySchema>

/** One group's figures (the whole company, a team, a person or a customer). */
export interface TaskReportFigures {
  open: number
  overdue: number
  unassigned: number
  blocked: number
  inReview: number
  /** Deliverables due in the period that were not cancelled. */
  dueInPeriod: number
  /** Of those, completed on or before their committed due date. */
  onTime: number
  /** onTime / dueInPeriod as a whole percentage; null when nothing was due. */
  onTimePercent: number | null
  completedInPeriod: number
}

export interface TaskReportRow extends TaskReportFigures {
  key: string
  label: string
  labelAr: string | null
}

export interface TaskReport {
  from: string
  to: string
  today: string
  totals: TaskReportFigures & {
    /** Open with no meaningful update for TASK_STALE_DAYS days. */
    stale: number
    /** Open customer work due in the next 7 days. */
    customerCommitmentsDue: number
    /** Median days from first start to completion (elapsed), of deliverables completed in the period. */
    cycleDaysMedian: number | null
    /** Median hours between submission and the review decision, in the period. */
    reviewHoursMedian: number | null
    /** Unassigned open work: the longest wait in days. */
    longestQueueDays: number | null
    /** Tasks returned by a reviewer or reopened, of those completed in the period. */
    reworked: number
    /** Completed in the period with both an estimate and actual effort. */
    effort: { tasks: number; estimateMinutes: number; actualMinutes: number }
  }
  byTeam: TaskReportRow[]
  byPerson: TaskReportRow[]
  byCustomer: TaskReportRow[]
  /** What the figures leave out, so nobody misreads them. */
  exclusions: {
    /** Parents with subtasks: their subtasks are counted instead. */
    parents: number
    /** Completed in the period with no committed due date (not in on-time figures). */
    noCommittedDate: number
    /** Completed in the period without an estimate or actual effort. */
    noEffort: number
  }
}

// ── errors ───────────────────────────────────────────────────────────────────

export const TASK_ERROR_CODES = [
  'TASK_MOVE_NOT_ALLOWED',
  'TASK_NOT_READY',
  'TASK_SUBTASKS_OPEN',
  'TASK_ALREADY_CLAIMED',
  'TASK_CLAIM_NOT_ALLOWED',
  'TASK_REVIEWER_IS_ASSIGNEE',
  'TASK_NOT_PERMITTED',
  'TASK_REASON_REQUIRED',
  'TASK_INVALID_PARENT',
  'TASK_INVALID_VISIBILITY',
  'TASK_INVALID_PERSON',
  'TASK_INVALID_LINK',
  'TASK_CLOSED',
  'TASK_NOT_BLOCKED',
  'TASK_ALREADY_BLOCKED',
  'TEAM_ARCHIVED',
] as const
export type TaskErrorCode = (typeof TASK_ERROR_CODES)[number]

// ── settings ─────────────────────────────────────────────────────────────────

export const taskSettingsSchema = z.object({
  /** New tasks need a reviewer's acceptance unless the creator says otherwise. */
  reviewRequiredByDefault: z.boolean(),
  /** Assignees may move a committed due date themselves (with a reason); otherwise they request it. */
  assigneeMayChangeDueDate: z.boolean(),
  /** Overdue this many days, the team's leads are told too; 0 never. */
  escalateOverdueAfterDays: z.number().int().min(0).max(30),
})
export type TaskSettings = z.output<typeof taskSettingsSchema>

// ── bulk actions ─────────────────────────────────────────────────────────────

/**
 * `POST /tasks/bulk`: the same commands as one task at a time, each in its own transaction, so one
 * refusal never blocks the others. Review cannot be skipped this way: a move goes through the move
 * table and the reviewer rule like any other.
 */
export const taskBulkSchema = z.object({
  items: z
    .array(z.object({ id: z.uuid(), version: versionSchema }))
    .min(1)
    .max(100),
  action: z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('assign'), assigneeId: z.uuid().nullable(), teamId: z.uuid().nullish() }),
    z.object({ kind: z.literal('priority'), priority: taskPrioritySchema }),
    z.object({ kind: z.literal('move'), to: taskStatusSchema, reason: optionalText(2_000) }),
  ]),
})
export type TaskBulkInput = z.input<typeof taskBulkSchema>

export interface TaskBulkResult {
  results: { id: string; ok: boolean; code: string | null; message: string | null }[]
}
